import { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import { useSnackbar } from "notistack";
import { createPlanAdmin, getAllPlansAdmin, getSinglePlansAdmin, updatePlanAdmin, removePackage } from "../shared/_services/common.services";
import Loader from "./Loader";
import { Link } from "react-router-dom";
import iconTrash from "../assets/images/trash-gif.gif";

const createInitialFormState = () => ({
    nickname: "",
    amount: "",
    features: [""],
    sequence: "",
    defaultMeetings: "",
    zoomCallsUnlimited: false,
    addOnMeetingPrice: "",
});

const toInputString = (value) => {
    if (value === null || value === undefined) {
        return "";
    }
    return typeof value === "string" ? value : String(value);
};

const normalizeFeatureList = (rawFeatures) => {
    if (Array.isArray(rawFeatures) && rawFeatures.length) {
        return rawFeatures;
    }
    if (typeof rawFeatures === "string" && rawFeatures.trim()) {
        return rawFeatures
            .split(/[|,\n]/)
            .map((item) => item.trim())
            .filter(Boolean);
    }
    return [""];
};

const mapPlanToFormState = (plan) => {
    if (!plan) {
        return createInitialFormState();
    }
    const metadata = plan?.metadata || plan?.product?.metadata || {};
    const featuresSource = plan?.features ?? metadata?.features ?? plan?.stripe?.metadata?.features ?? [];
    const amountValue = plan?.amount ?? (typeof plan?.stripe?.unit_amount === "number" ? plan.stripe.unit_amount / 100 : metadata?.price);
    const zoomCallsUnlimited = Boolean(plan?.zoomCallsUnlimited ?? metadata?.zoomCallsUnlimited);
    
    return {
        nickname: toInputString(plan?.nickname || plan?.stripe?.nickname || metadata?.nickname || ""),
        amount: toInputString(amountValue),
        features: normalizeFeatureList(featuresSource),
        sequence: toInputString(plan?.sequence ?? metadata?.sequence ?? ""),
        defaultMeetings: toInputString(
            zoomCallsUnlimited
                ? 0
                : (plan?.defaultMeetings ?? metadata?.defaultMeetings ?? metadata?.default_meetings ?? "")
        ),
        zoomCallsUnlimited,
        addOnMeetingPrice: toInputString(plan?.addOnMeetingPrice ?? metadata?.addOnMeetingPrice ?? metadata?.addOnPrice ?? ""),
    };
};

/** Display formatting for package table currency columns */
function formatPackageTableCurrency(value) {
    if (typeof value === "number") {
        return value.toFixed(2);
    }
    if (typeof value === "string" && value) {
        const numeric = Number(value);
        return Number.isNaN(numeric) ? value : numeric.toFixed(2);
    }
    return "--";
}

/** Features list for admin table (differs from form normalizeFeatureList empty handling) */
function parseFeaturesForPackageTable(rawFeatures) {
    if (Array.isArray(rawFeatures)) {
        return rawFeatures;
    }
    if (typeof rawFeatures === "string") {
        return rawFeatures.split(/[|,\n]/).map((item) => item.trim()).filter(Boolean);
    }
    return [];
}

function resolvePackagePriceCandidates(pkg, metadata, stripeInfo) {
    const amountFromStripe =
        typeof stripeInfo?.unit_amount === "number" ? stripeInfo.unit_amount / 100 : null;
    const amountFromPkgField = typeof pkg?.amount === "number" ? pkg.amount : null;
    const amountFromMetadata = typeof metadata?.price === "number" ? metadata.price : null;
    return amountFromPkgField ?? amountFromStripe ?? amountFromMetadata;
}

function normalizeAdminPackageRow(pkg, index) {
    const metadata = pkg?.metadata || pkg?.product?.metadata || {};
    const stripeInfo = pkg?.stripe || {};
    const rawFeatures =
        pkg?.features || metadata?.features || stripeInfo?.metadata?.features || [];

    const featureList = parseFeaturesForPackageTable(rawFeatures);
    const price = resolvePackagePriceCandidates(pkg, metadata, stripeInfo);
    const addOnPrice = pkg?.addOnMeetingPrice ?? metadata?.addOnMeetingPrice ?? metadata?.addOnPrice;
    const planRecordId = pkg?._id || pkg?.id || pkg?.stripePriceId || pkg?.lookup_key || `pkg-${index}`;

    return {
        id: planRecordId,
        planRecordId,
        name: pkg?.nickname || stripeInfo?.nickname || pkg?.lookup_key || pkg?.product?.name || "Package",
        price: formatPackageTableCurrency(price),
        defaultMeetings: pkg?.defaultMeetings ?? metadata?.defaultMeetings ?? metadata?.default_meetings ?? "-",
        zoomCallsUnlimited: Boolean(pkg?.zoomCallsUnlimited ?? metadata?.zoomCallsUnlimited ?? false),
        addOnPrice: formatPackageTableCurrency(addOnPrice),
        features: featureList,
        sequence: pkg?.sequence ?? metadata?.sequence,
    };
}

function isPackageFormValueEmpty(value) {
    return (
        value === "" ||
        value === null ||
        value === undefined ||
        (typeof value === "string" && !value.trim())
    );
}

function validateRequiredPositiveNumber(value, emptyMessage, invalidMessage) {
    if (isPackageFormValueEmpty(value)) return emptyMessage;
    const num = Number(value);
    if (Number.isNaN(num) || num <= 0) return invalidMessage;
    return undefined;
}

function validateRequiredNonNegativeNumber(value, emptyMessage, invalidMessage) {
    if (isPackageFormValueEmpty(value)) return emptyMessage;
    const num = Number(value);
    if (Number.isNaN(num) || num < 0) return invalidMessage;
    return undefined;
}

function packageFormFieldErrorsNickname(values) {
    if (!values.nickname?.trim()) return { nickname: "Package name is required" };
    return {};
}

function packageFormFieldErrorsAmount(values) {
    const msg = validateRequiredPositiveNumber(
        values.amount,
        "Amount is required",
        "Amount must be greater than 0"
    );
    return msg ? { amount: msg } : {};
}

function packageFormFieldErrorsSequence(values) {
    const msg = validateRequiredPositiveNumber(
        values.sequence,
        "Sequence is required",
        "Sequence must be greater than 0"
    );
    return msg ? { sequence: msg } : {};
}

function packageFormFieldErrorsDefaultMeetings(values) {
    if (values.zoomCallsUnlimited) return {};
    const msg = validateRequiredNonNegativeNumber(
        values.defaultMeetings,
        "Default meetings is required",
        "Default meetings cannot be negative"
    );
    return msg ? { defaultMeetings: msg } : {};
}

function packageFormFieldErrorsAddOn(values) {
    const msg = validateRequiredNonNegativeNumber(
        values.addOnMeetingPrice,
        "Add-on price is required",
        "Add-on price cannot be negative"
    );
    return msg ? { addOnMeetingPrice: msg } : {};
}

function packageFormFieldErrorsFeatures(values) {
    const nonEmpty = (values.features || []).filter((feature) => feature?.trim());
    if (!nonEmpty.length) return { features: "At least one feature is required" };
    return {};
}

export function validatePackageManagementForm(values) {
    return {
        ...packageFormFieldErrorsNickname(values),
        ...packageFormFieldErrorsAmount(values),
        ...packageFormFieldErrorsSequence(values),
        ...packageFormFieldErrorsDefaultMeetings(values),
        ...packageFormFieldErrorsAddOn(values),
        ...packageFormFieldErrorsFeatures(values),
    };
}

// No `setupFeeEnable` / `setupFeePrice`. The setup fee is one platform-level
// amount configured from ChangeSubscriptionPrice.jsx (backend SEC-110 /
// INT-046); the per-plan fields are still accepted and stored by the API but no
// billing path reads them, so the controls that set them here only ever produced
// a number an admin could see and a customer would never be charged.
export function buildPackageSavePayload(values) {
    return {
        nickname: values.nickname.trim(),
        amount: Number(values.amount),
        currency: "USD",
        features: values.features.map((feature) => feature.trim()).filter(Boolean),
        sequence: Number(values.sequence),
        defaultMeetings: values.zoomCallsUnlimited ? 0 : Number(values.defaultMeetings),
        zoomCallsUnlimited: Boolean(values.zoomCallsUnlimited),
        addOnMeetingPrice: Number(values.addOnMeetingPrice),
    };
}

function buildEditingPlanIdentifiers(editingPlanId, activePlanMeta) {
    if (!editingPlanId) {
        return {};
    }
    const stripeLikeId =
        activePlanMeta?.stripePriceId ||
        activePlanMeta?.stripe?.id ||
        activePlanMeta?.id ||
        editingPlanId;
    return {
        planId: editingPlanId,
        id: editingPlanId,
        _id: editingPlanId,
        stripePriceId: stripeLikeId,
        priceId: stripeLikeId,
    };
}

async function savePackageRequest(editingPlanId, payload, identifiers) {
    if (editingPlanId) {
        return updatePlanAdmin({
            id: editingPlanId,
            data: {
                ...payload,
                ...identifiers,
            },
        });
    }
    return createPlanAdmin(payload);
}

function resolveModalPrimaryButtonLabel(isSubmitting, isEditMode) {
    if (isSubmitting) {
        return "Saving...";
    }
    return isEditMode ? "Update Package" : "Save Package";
}

function toggleZoomUnlimitedMeetings(setFieldValue, zoomCallsUnlimited) {
    const nextValue = !zoomCallsUnlimited;
    setFieldValue("zoomCallsUnlimited", nextValue);
    if (nextValue) {
        setFieldValue("defaultMeetings", 0);
    }
}

function FormToggleSwitch({ checked, onClick }) {
    const trackColor = checked ? "#63363A" : "#D3D3D3";
    const circleCx = checked ? "47.5" : "12.5";
    return (
        <svg
            width="60"
            height="24"
            viewBox="0 0 60 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={onClick}
            style={{ cursor: "pointer" }}
        >
            <rect
                x="1.25"
                y="1.25"
                width="57.5"
                height="21.5"
                rx="10.75"
                fill={trackColor}
                stroke={trackColor}
                strokeWidth="2.5"
            />
            <circle cx={circleCx} cy="12" r="10" fill="white" />
        </svg>
    );
}

function PackageFeaturesFieldArraySection({ values, remove, push }) {
    return (
        <>
            {values.features.map((feature, index) => (
                <div className="d-flex gap-15 feature-input-box mb-2" key={feature}>
                    <Field
                        type="text"
                        name={`features[${index}]`}
                        className="form-control"
                        placeholder="Enter feature"
                    />
                    <button
                        type="button"
                        onClick={() => remove(index)}
                        disabled={values.features.length === 1}
                    >
                        <i className="iconoir-trash"></i>
                    </button>
                </div>
            ))}
            <ErrorMessage name="features" component="small" className="text-danger d-block mb-2" />
            <button
                type="button"
                className="add-feature-btn"
                onClick={() => push("")}
                disabled={values.features.some((feature) => !feature?.trim())}
            >
                + Add Feature
            </button>
        </>
    );
}

function PackageManagementModalFormContent({ values, setFieldValue, isSubmitting, isEditMode, handleClose }) {
    const modalPrimaryButtonLabel = resolveModalPrimaryButtonLabel(isSubmitting, isEditMode);
    return (
        <Form>
            <div className="row">
                <div className="col-md-12 pr-0">
                    <div className="form-group mb-3">
                        <label className="form-label mb-1">Package Name</label>
                        <Field
                            type="text"
                            name="nickname"
                            className="form-control"
                            placeholder="Enter package name"
                        />
                        <ErrorMessage name="nickname" component="small" className="text-danger" />
                    </div>
                </div>
                <div className="col-md-6 pr-0">
                    <div className="form-group mb-3 position-relative">
                        <label className="form-label mb-1">Price</label>
                        <Field
                            type="number"
                            name="amount"
                            className="form-control"
                            placeholder="Enter amount"
                        />
                        <div className="default-month-box">Per Month</div>
                        <ErrorMessage name="amount" component="small" className="text-danger d-block" />
                    </div>
                </div>
                <div className="col-md-6 pr-0"></div>
                <div className="col-md-6">
                    <div className="form-group mb-3">
                        <label className="form-label mb-2 w-100">Unlimited Meetings</label>
                        <FormToggleSwitch
                            checked={values.zoomCallsUnlimited}
                            onClick={() => toggleZoomUnlimitedMeetings(setFieldValue, values.zoomCallsUnlimited)}
                        />
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group mb-3">
                        <label className="form-label mb-1">Default Meetings</label>
                        <Field
                            type="number"
                            name="defaultMeetings"
                            className="form-control"
                            placeholder="Enter value"
                            disabled={values.zoomCallsUnlimited}
                        />
                        <ErrorMessage name="defaultMeetings" component="small" className="text-danger" />
                    </div>
                </div>

                <div className="col-md-6">
                    <div className="form-group mb-3">
                        <label className="form-label mb-1">Sequence</label>
                        <Field
                            type="number"
                            name="sequence"
                            className="form-control"
                            placeholder="Enter value"
                        />
                        <ErrorMessage name="sequence" component="small" className="text-danger" />
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="form-group mb-3">
                        <label className="form-label mb-1">Add-on Meeting Price</label>
                        <Field
                            type="number"
                            name="addOnMeetingPrice"
                            className="form-control"
                            placeholder="Enter Price"
                        />
                        <ErrorMessage name="addOnMeetingPrice" component="small" className="text-danger" />
                    </div>
                </div>
                <div className="col-md-12 mb-3">
                    <label className="form-label mb-1">Features List</label>
                    <FieldArray name="features">
                        {({ remove, push }) => (
                            <PackageFeaturesFieldArraySection values={values} remove={remove} push={push} />
                        )}
                    </FieldArray>
                </div>
            </div>
            <div className="modal-footer d-flex justify-content-end border-0 pt-0 px-0 add-package-form-popup-footer">
                <button type="button" className="as-btn-secondary mr-2 py-2 px-4" onClick={handleClose} disabled={isSubmitting}>
                    Cancel
                </button>
                <button type="submit" className="as-btn-primary mr-2 py-2 px-4" disabled={isSubmitting}>
                    {modalPrimaryButtonLabel}
                </button>
            </div>
        </Form>
    );
}

const PackageManagement = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [show, setShow] = useState(false);
    const [packages, setPackages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState("");
    const [formInitialValues, setFormInitialValues] = useState(createInitialFormState);
    const [editingPlanId, setEditingPlanId] = useState(null);
    const [activePlanMeta, setActivePlanMeta] = useState(null);
    const [isPlanDetailsLoading, setIsPlanDetailsLoading] = useState(false);
    const [confirmDocumentDeletePopup, setConfirmDocumentDeletePopup] = useState(false);
    const [packageIdToDelete, setPackageIdToDelete] = useState(null);

    const documentDeletePopupClose = () => {
        setConfirmDocumentDeletePopup(false);
        setPackageIdToDelete(null);
    };

    const handleDelete = async () => {
        if (!packageIdToDelete) {
            enqueueSnackbar("Unable to delete package. Missing identifier.", { variant: "error" });
            return;
        }
        try {
            const response = await removePackage(packageIdToDelete);
            if (response?.status >= 200 && response?.status < 300) {
                enqueueSnackbar("Package deleted successfully.", { variant: "success" });
                documentDeletePopupClose();
                fetchPackages();
            } else {
                enqueueSnackbar(response?.response?.data?.message || "Unable to delete package.", { variant: "error" });
            }
        } catch (error) {
            enqueueSnackbar("Unable to delete package.", { variant: "error" });
            console.error("Failed to delete package", error);
        }
    };


    const openDeletePopup = (packageId) => {
        setPackageIdToDelete(packageId);
        setConfirmDocumentDeletePopup(true);
    };

    const resetForm = () => {
        setFormInitialValues(createInitialFormState());
    };

    const handleClose = () => {
        setShow(false);
        resetForm();
        setEditingPlanId(null);
        setActivePlanMeta(null);
        setIsPlanDetailsLoading(false);
    };
    const handleShow = () => {
        resetForm();
        setEditingPlanId(null);
        setActivePlanMeta(null);
        setIsPlanDetailsLoading(false);
        setShow(true);
    };

    const handleEditPackage = async (planId) => {
        if (!planId) {
            enqueueSnackbar("Unable to edit this package. Missing identifier.", { variant: "error" });
            return;
        }
        resetForm();
        setEditingPlanId(planId);
        setActivePlanMeta(null);
        setShow(true);
        setIsPlanDetailsLoading(true);
        try {
            const response = await getSinglePlansAdmin(planId);
            if (response?.status === 200 && response?.data?.data) {
                const planDetails = response.data.data;
                setActivePlanMeta(planDetails);
                setFormInitialValues(mapPlanToFormState(planDetails));
            } else {
                enqueueSnackbar("Unable to load package details.", { variant: "error" });
                handleClose();
            }
        } catch (error) {
            enqueueSnackbar("Unable to load package details.", { variant: "error" });
            console.error("Failed to fetch plan details", error);
            handleClose();
        } finally {
            setIsPlanDetailsLoading(false);
        }
    };

    const fetchPackages = useCallback(async () => {
        setIsLoading(true);
        setFetchError("");
        try {
            const response = await getAllPlansAdmin();
            if (response?.status === 200 && Array.isArray(response?.data?.data)) {
                setPackages(response.data.data);
            } else {
                setFetchError("Unable to fetch packages. Please try again.");
            }
        } catch (error) {
            setFetchError("Unable to fetch packages. Please try again.");
            console.error("Failed to load packages", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPackages();
    }, [fetchPackages]);

    const normalizedPackages = useMemo(
        () => packages.map((pkg, index) => normalizeAdminPackageRow(pkg, index)),
        [packages]
    );

    const handleSavePackage = async (values, { setSubmitting, resetForm }) => {
        const payload = buildPackageSavePayload(values);
        const identifiers = buildEditingPlanIdentifiers(editingPlanId, activePlanMeta);
        const saveVerb = editingPlanId ? "update" : "create";

        try {
            const response = await savePackageRequest(editingPlanId, payload, identifiers);
            const isSuccess = response?.status >= 200 && response?.status < 300;
            if (isSuccess) {
                enqueueSnackbar(
                    editingPlanId ? "Package updated successfully." : "Package created successfully.",
                    { variant: "success" }
                );
                resetForm({ values: createInitialFormState() });
                handleClose();
                fetchPackages();
            } else {
                enqueueSnackbar(
                    response?.response?.data?.message || `Unable to ${saveVerb} package.`,
                    { variant: "error" }
                );
            }
        } catch (error) {
            enqueueSnackbar(`Unable to ${saveVerb} package.`, { variant: "error" });
            console.error("Failed to save package", error);
        } finally {
            setSubmitting(false);
        }
    };

    const isEditMode = Boolean(editingPlanId);

    return (
        <>
            <div className="page-header d-flex align-items-center justify-content-between">
                <h2>Packages Management</h2>
                <button type="button" onClick={handleShow} className="as-btn-primary btn-sharp mt-1 active">
                    Add New Package
                </button>
            </div>
            <div className="p-4">
                {isLoading ? (
                    <Loader />
                ) : (
                    <>
                        {fetchError && <div className="alert alert-danger py-2">{fetchError}</div>}
                        <div className='row'>
                            {normalizedPackages.length === 0 && !fetchError && (
                                <div className="col-12">
                                    <h2 className="text-center text-astra py-5">
                                        No packages found.
                                    </h2>
                                </div>
                            )}
                            {normalizedPackages.map((pkg) => (
                        <div className='col-md-4 mb-4 d-flex' key={pkg.id}>
                            <div className='admin-package-card flex-grow-1'>
                                <div>
                                    <div className='package-card-header mb-0'>
                                        <div>
                                            <h6 className='mb-1'>{pkg.name}</h6>
                                            <h3 className='f-500 mb-0'>
                                                <b className="text-astra">${pkg.price || "--"}</b>
                                                <span className='text-12 text-secondary'>/month</span>
                                            </h3>
                                        </div>
                                        <div>
                                            <p className='text-12 text-secondary text-right mb-1 mt-1'>Default Meetings</p>
                                            <h5 className='text-2 text-right f-600 mb-0 text-astra'>
                                                {pkg.zoomCallsUnlimited ? "Unlimited" : (pkg.defaultMeetings ?? "-")}
                                            </h5>
                                        </div>
                                    </div>
                                    <div className='px-4 py-3 pb-0'>
                                        <p className='text-12 text-secondary f-500 mb-2 text-uppercase'>Features:</p>
                                        {pkg.features?.length ? (
                                            <ul className='pl-4'>
                                                {pkg.features.map((feature, idx) => (
                                                    <li className='text-12 f-400' key={feature}>
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className='text-12 text-secondary'>No features listed.</p>
                                        )}
                                        <p className='text-12 text-secondary f-500 mb-2'>
                                            Add-on Meeting Price : ${pkg.addOnPrice || "--"}/Each Slot
                                        </p>
                                    </div>
                                </div>

                                <div className='package-card-action-btns'>
                                    <button
                                        type="button"
                                        className="as-btn-secondary mr-2 py-2 px-2 w-100"
                                        onClick={() => handleEditPackage(pkg.planRecordId)}
                                        disabled={!pkg.planRecordId}
                                    >
                                        Edit Package
                                    </button>
                                    <button
                                        type="button"
                                        className="as-btn-primary mr-2 py-2 px-2 w-100"
                                        onClick={() => openDeletePopup(pkg.planRecordId)}
                                        disabled={!pkg.planRecordId}
                                    >
                                        Delete Package
                                    </button>
                                </div>
                            </div>
                        </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            <Modal
                className="notificationPopup fade-right"
                show={show}
                onHide={handleClose}
                aria-labelledby="notificationPopupLabel"
                keyboard={false}
                centered
            >
                <div className="modal-body">
                    <div className="d-flex mb-2 justify-content-between">
                        <h4 className="ast-card-heading w-100 mb-2">{isEditMode ? "Edit Package" : "Add Package"}</h4>
                        <a
                            className="close-popup-btn"
                            onClick={handleClose}
                        >
                            <i className="iconoir-xmark"></i>
                        </a>
                    </div>

                    <div className="add-package-form-popup-body">
                        {isPlanDetailsLoading ? (
                            <Loader />
                        ) : (
                            <Formik
                                enableReinitialize
                                initialValues={formInitialValues}
                                validate={validatePackageManagementForm}
                                onSubmit={handleSavePackage}
                                validateOnMount={false}
                            >
                                {(formikProps) => (
                                    <PackageManagementModalFormContent
                                        {...formikProps}
                                        isEditMode={isEditMode}
                                        handleClose={handleClose}
                                    />
                                )}
                            </Formik>
                        )}
                    </div>
                </div>
            </Modal>


            {/* confirm delete popup */}
            <Modal
                size="md"
                className="confirmLogoutPopup customMiniPopup"
                show={confirmDocumentDeletePopup}
                onHide={documentDeletePopupClose}
                aria-labelledby="confirmLogoutPopupLabel"
                backdrop="static"
                keyboard={false}
                centered
            >
                <div className="modal-body">
                    <div className="d-flex justify-content-between">
                        <h4 className="mt-3">  </h4>
                        <Link className="close-popup-btn" onClick={documentDeletePopupClose}>
                            <i className="iconoir-xmark"></i>
                        </Link>
                    </div>
                    <div className="d-flex justify-content-center">
                        <img width={43} src={iconTrash} alt="trash" />
                    </div>

                    <h5 className="mt-3 mb-2 text-center f-400 text-secondary">You are attempting to delete this document.</h5>
                    <h5 className="text-center f-700">Are you sure?</h5>
                    <div className="text-center mt-4 mb-3">
                        <button className="as-btn-primary px-4" onClick={handleDelete}>
                            <i className="fa fa-check mr-2" aria-hidden="true"></i>
                            Confirm
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default PackageManagement;